'use client';

import * as React from 'react';
import { cn } from '@/components/atoms/primitives/utils';

/**
 * Discriminated identifier for the filter facet that should be cleared when a
 * chip's `×` is activated. Multi-select facets carry the value to remove;
 * single-value facets carry only the key.
 */
export type DiscoverActiveFilterRemoveKey =
  | {
      readonly kind: 'multi';
      // WEB-741: `venue` added — it lost its only UI producer when WEB-662
      // retired the followed-venues rail. Mirrors `MULTI_KEYS`.
      readonly key: 'category' | 'neighborhood' | 'venue';
      readonly value: string;
    }
  | {
      readonly kind: 'single';
      // Mirrors `SINGLE_KEYS` in `lib/discover/activeFilterCount.ts` — keep the
      // two in step (`followedOnly` added by WEB-662 / DPL-12).
      readonly key: 'date' | 'price' | 'q' | 'followedOnly';
    };

export interface DiscoverActiveFilter {
  /** Stable React key, e.g. `"category:music"` or `"date"`. */
  readonly key: string;
  /** User-visible chip label (e.g. `"Category: Music"`). */
  readonly label: string;
  /** Opaque token forwarded to `onRemove` when the × is activated. */
  readonly removeKey: DiscoverActiveFilterRemoveKey;
}

export interface DiscoverActiveFiltersViewProps {
  /** Active filters in render order. */
  readonly activeFilters: ReadonlyArray<DiscoverActiveFilter>;
  /** Called once with the chip's `removeKey` when the × is activated. */
  readonly onRemove: (removeKey: DiscoverActiveFilterRemoveKey) => void;
  /**
   * FRE-912 — render the `Matched to taste` pseudo-filter chip ahead of the
   * URL-derived chips. It is NOT a URL param: the connected wrapper derives it
   * from the shared `/events/discover` subscription (the authenticated viewer
   * has a taste signal AND results are taste-ranked). Defaults to `false`, so
   * anonymous / signal-less viewers — and any story / test that omits it — keep
   * the pre-FRE-912 plain chip row. Matches DS `disc-rail__chip-row` (L912–919).
   */
  readonly tasteActive?: boolean;
  /**
   * Called when the `Matched to taste` chip's × is activated. When provided the
   * pseudo-chip renders removable (honest mapping: the wrapper turns off
   * taste-ranking via the sort URL state). When omitted the chip is presentation
   * only — still rendered when `tasteActive`, but with no clear affordance.
   */
  readonly onRemoveTaste?: () => void;
  /** Wrapper class names. */
  readonly className?: string;
}

/** Stable chip label + accessible-name fragment for the taste pseudo-filter. */
const TASTE_CHIP_LABEL = 'Matched to taste';

/**
 * Pure presentational chip row for the Discover filter rail. Renders nothing
 * when there are no active filters so the rail collapses cleanly.
 *
 * WEB-347 (2026-05-24): Chip + summary styling now matches the canonical
 * `.disc-rail__active` and `.disc-rail__chip-row b` tokens from
 * `docs/design-system/1.1.0/templates/app/discover.html` (lines 142-166 +
 * 918-925). Background flipped from the invisible `skyline-wash` to the solid
 * `--clt-skyline` token with white text; summary header now uses the template's
 * mono 10px / 0.16em / uppercase / `--fg-3` treatment and occupies its own row
 * above the chip wrap (mirroring the template's `width: 100%` rule on the
 * preceding `<b>`).
 */
export function DiscoverActiveFiltersView({
  activeFilters,
  onRemove,
  tasteActive = false,
  onRemoveTaste,
  className,
}: DiscoverActiveFiltersViewProps): React.ReactElement | null {
  // The taste pseudo-chip counts toward the row even when no URL filters are
  // active, so the row must render when EITHER is present (DS shows the chip in
  // the `4 active filters` row alongside the URL-derived chips).
  if (activeFilters.length === 0 && !tasteActive) return null;

  const count = activeFilters.length + (tasteActive ? 1 : 0);
  const tasteRemovable = onRemoveTaste !== undefined;

  return (
    <section
      data-testid="discover-active-filters"
      aria-label="Active filters"
      className={cn('flex flex-col gap-1', className)}
    >
      <p
        data-testid="discover-active-filters-summary"
        // Template: `.disc-rail__chip-row b` (lines 142-150) —
        //   width:100%; font-family:var(--font-mono); font-size:10px;
        //   color:var(--fg-3); letter-spacing:0.16em;
        //   margin-bottom:4px; text-transform:uppercase;
        className="mb-1 w-full font-mono text-micro tracking-[0.16em] text-fg-3"
      >
        {count} active filter{count === 1 ? '' : 's'}
      </p>
      <div
        data-testid="discover-active-filters-chip-row"
        className="flex items-start justify-between gap-2"
      >
        <ul
          data-testid="discover-active-filters-list"
          className="flex min-w-0 flex-1 flex-wrap gap-1.5"
        >
          {tasteActive ? (
            <li className="contents">
              {/* FRE-912: `Matched to taste` pseudo-filter chip. Template
                `.disc-rail__active` with the glow override (L915):
                  background: var(--accent); color: var(--bg);
                — a bright teal pill that reads as a distinct "your taste is
                shaping these results" signal vs the solid-skyline removable
                facet chips. Rendered first to mirror the DS row order. */}
              {tasteRemovable ? (
                <button
                  type="button"
                  onClick={onRemoveTaste}
                  data-testid="discover-active-filters-chip-taste"
                  aria-label={`Remove filter: ${TASTE_CHIP_LABEL}`}
                  className={cn(
                    // WEB-1076: the DS 32px chip on both pointers; `.a-hit-floor`
                    // is the coarse-pointer 44×44 hit box (see the facet chips below).
                    'a-hit-floor inline-flex min-h-8 touch-manipulation items-center gap-1.5 rounded-full px-3 py-1',
                    'font-mono text-micro tracking-[0.04em]',
                    // DS L915 glow override — `var(--accent)` fill with
                    // the page-background colour as text. Both tokens are
                    // theme-aware, so the pill stays legible in dark + light.
                    'bg-ds-accent text-background',
                    'hover:bg-clt-skyline-light focus-visible:bg-clt-skyline-light',
                    'transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring focus-visible:ring-offset-2',
                  )}
                >
                  <span>{TASTE_CHIP_LABEL}</span>
                  <span aria-hidden="true" className="text-sm leading-none opacity-70">
                    ×
                  </span>
                </button>
              ) : (
                <span
                  data-testid="discover-active-filters-chip-taste"
                  className={cn(
                    'inline-flex items-center rounded-full px-3 py-1',
                    'font-mono text-micro tracking-[0.04em]',
                    'bg-ds-accent text-background',
                  )}
                >
                  {TASTE_CHIP_LABEL}
                </span>
              )}
            </li>
          ) : null}
          {activeFilters.map((filter) => (
            <li key={filter.key} className="contents">
              <button
                type="button"
                onClick={() => onRemove(filter.removeKey)}
                data-testid={`discover-active-filters-chip-${filter.key}`}
                aria-label={`Remove filter: ${filter.label}`}
                // Template: `.disc-rail__active` (lines 151-160) —
                //   padding:4px 12px; background:var(--clt-skyline); color:#FFF;
                //   border-radius:var(--r-pill); font-family:var(--font-mono);
                //   font-size:11px; letter-spacing:0.04em;
                //   display:inline-flex; align-items:center; gap:6px;
                // WEB-275 carry-over: no `uppercase` so humanised labels
                // (`Date: Next 7 days`, `NoDa`) keep their intended casing.
                className={cn(
                  // WEB-1076: the DS 32px chip (atoms ruling: chips are 32)
                  // on both pointers. The 44×44 touch floor is the
                  // coarse-pointer `.a-hit-floor` `::before` (atoms.css). At
                  // 32px the guard reaches 6px past each edge, exactly the
                  // list's 6px wrap gap, so a chip's guard never covers the
                  // paint of the chip on the next line (the later chip would
                  // win that tap). Keep paint ≥ 32 while the gap is 6px.
                  'a-hit-floor inline-flex min-h-8 touch-manipulation items-center gap-1.5 rounded-full px-3 py-1',
                  'font-mono text-micro tracking-[0.04em]',
                  'bg-clt-skyline text-white',
                  'hover:bg-clt-skyline-deep focus-visible:bg-clt-skyline-deep',
                  'transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clt-skyline focus-visible:ring-offset-2',
                )}
              >
                <span>{filter.label}</span>
                {/* Template: `.disc-rail__active::after` (lines 161-165) —
                  content:'×'; font-size:14px; line-height:1; opacity:0.7; */}
                <span aria-hidden="true" className="text-sm leading-none opacity-70">
                  ×
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
